import { jsx, jsxs } from 'react/jsx-runtime';
import { DialogContent, DialogTitle, DialogDescription } from '@radix-ui/react-dialog';
import { motion } from 'framer-motion';
import React__default, { useEffect, useCallback } from 'react';
import { keyframes, styled } from 'styled-components';
import { ResourceViewMobile } from './MobileView/ResourceViewMobile.js';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { Modal, defaultModalContentProps } from '@fabric-app/ui/components/Modal/Modal';
import { useSyncExpandedResourceIdWithRouter, useExpandedResource, useExpandedResourceStore, useShowExpandedResource } from '../../../resources/stores/useExpandedResourceStore.js';
import { useResourceSwitcher } from '../../contexts/resourcesSwitcher.js';
import { useDebouncedCallback } from '@fabric-app/hooks/useDebouncedCallback';
import { isNumber } from '@fabric-app/utils/guards';
import { createLogger } from '@fabric-app/utils/logging';
import { pick } from '@fabric-app/utils/object/manipulation';
import { useDebounce } from '@fabric-app/hooks/useDebounce';

const inAnimation = keyframes([
  `from{opacity:0;}to{opacity:1;}`
]);
const outAnimation = keyframes([
  `from{opacity:1;}to{opacity:0;}`
]);
const ITEMS_GAP = 20;
const OVERSCAN = 1;
const OVERSCAN_FETCH_NEXT_PAGE = OVERSCAN + 6;
const EXTRA_SIDE_SPACING = 150;
const Item = styled(motion.div).withConfig({
  displayName: "ModalResource__Item",
  componentId: "sc-719a8104-0"
})([
  `width:100vw;height:100vh;overflow:hidden;display:flex;flex-direction:column;flex-shrink:0;z-index:100;left:0;top:0;box-shadow:0 0 20px 0 rgba(`,
  `,0.1);scroll-snap-align:start;position:absolute;`
], cssVar["color-bg-primary-reverse-rgb"]);
const ScrollContainer = styled.div.withConfig({
  displayName: "ModalResource__ScrollContainer",
  componentId: "sc-719a8104-1"
})([
  `width:100vw;height:100vh;position:relative;background:black;overflow-x:scroll;scroll-snap-type:x mandatory;-ms-overflow-style:none;scrollbar-width:none;&::-webkit-scrollbar{display:none;}`
]);
const ModalDialog = styled(DialogContent).withConfig({
  displayName: "ModalResource__ModalDialog",
  componentId: "sc-719a8104-2"
})([
  `position:fixed;top:0;left:0;width:100%;height:100%;z-index:125;transition:none;overscroll-behavior:contain;-webkit-overflow-scrolling:touch !important;&[data-state='open']{animation:`,
  ` 0.2s ease-out forwards;animation-delay:0s;}&[data-state='closed']{animation:`,
  ` 0.2s ease-out forwards;animation-delay:0s;}`
], inAnimation, outAnimation);
const DisabledTouchAreaOverlay = styled.div.withConfig({
  displayName: "ModalResource__DisabledTouchAreaOverlay",
  componentId: "sc-719a8104-3"
})([
  `position:fixed;top:0;height:100vh;width:30px;z-index:300;touch-action:none;`
]);
const logger = createLogger("ModalResource mobile", true);
const ModalResource = () => {
  useSyncExpandedResourceIdWithRouter();
  const expandedResourceId = useExpandedResource();
  const { expandedResourceDisableSwicher: expandedResourceDisableSwicherRaw } = useExpandedResourceStore((s) => pick(s, [
    "expandedResourceDisableSwicher"
  ]));
  const debouncedExpandedResourceDisableSwicher = useDebounce(expandedResourceDisableSwicherRaw, 300);
  const expandedResourceDisableSwicher = expandedResourceDisableSwicherRaw || debouncedExpandedResourceDisableSwicher;
  const showExpandedResource = useShowExpandedResource();
  const { resourceIds: switcherResourceIds, removeByResourceId: removeFromSwitcherByResourceId, fetchNextPage } = useResourceSwitcher();
  const [state, setState] = React__default.useState({
    expandedResourceSwitcherIndex: void 0,
    isInitiallyPositioned: false
  });
  useEffect(() => {
    if (!expandedResourceId) {
      setState({
        expandedResourceSwitcherIndex: void 0,
        isInitiallyPositioned: false
      });
    } else {
      if (expandedResourceDisableSwicher) {
        setState({
          expandedResourceSwitcherIndex: -1,
          isInitiallyPositioned: false
        });
        return;
      }
      const foundIndex = switcherResourceIds.findIndex((item) => item === expandedResourceId);
      if (foundIndex === -1) {
        setState({
          expandedResourceSwitcherIndex: -1,
          isInitiallyPositioned: false
        });
        return;
      }
      setState((state2) => {
        const activeIndex2 = state2.expandedResourceSwitcherIndex ?? -1;
        const distanceDiff = Math.abs(activeIndex2 - foundIndex);
        return {
          isInitiallyPositioned: distanceDiff > 1 || activeIndex2 === -1 ? false : true,
          expandedResourceSwitcherIndex: foundIndex
        };
      });
    }
  }, [
    expandedResourceDisableSwicher,
    expandedResourceId,
    switcherResourceIds
  ]);
  const onScrollContainerRefChange = useCallback((containerEl) => {
    if (containerEl && !state.isInitiallyPositioned) {
      logger.log("onScrollContainerRefChange, initializing position");
      if (isNumber(state.expandedResourceSwitcherIndex) && !state.isInitiallyPositioned && state.expandedResourceSwitcherIndex > -1 && !expandedResourceDisableSwicher) {
        containerEl.scrollTo({
          left: EXTRA_SIDE_SPACING + (containerEl.clientWidth + ITEMS_GAP) * state.expandedResourceSwitcherIndex
        });
        setTimeout(() => {
          setState((prevState) => {
            logger.log("set isInitiallyPositioned to true");
            return {
              ...prevState,
              isInitiallyPositioned: true
            };
          });
        }, 150);
      }
    }
  }, [
    state,
    expandedResourceDisableSwicher
  ]);
  const closeModal = React__default.useCallback(() => {
    setState({
      expandedResourceSwitcherIndex: void 0,
      isInitiallyPositioned: false
    });
    showExpandedResource(null);
  }, [
    showExpandedResource
  ]);
  const handleOnDelete = React__default.useCallback((id) => {
    removeFromSwitcherByResourceId(id);
    closeModal();
  }, [
    closeModal,
    removeFromSwitcherByResourceId
  ]);
  const debouncedSetexpandedResourceId = useDebouncedCallback(showExpandedResource, 50);
  const activeIndex = expandedResourceDisableSwicher ? -1 : state.expandedResourceSwitcherIndex;
  const { resourcesToRender } = React__default.useMemo(() => {
    if (expandedResourceDisableSwicher && expandedResourceId) {
      return {
        resourcesToRender: [
          {
            id: expandedResourceId,
            switcherIndex: -1
          }
        ]
      };
    }
    const expandedResourceSwitcherIndex = state.expandedResourceSwitcherIndex;
    if (!isNumber(expandedResourceSwitcherIndex)) {
      return {
        resourcesToRender: []
      };
    }
    if (expandedResourceSwitcherIndex === -1) {
      if (!expandedResourceId) {
        return {
          resourcesToRender: []
        };
      }
      return {
        resourcesToRender: [
          {
            id: expandedResourceId,
            switcherIndex: 0
          }
        ]
      };
    } else {
      const resourcesToRender2 = [];
      for (let i = expandedResourceSwitcherIndex - OVERSCAN; i <= expandedResourceSwitcherIndex + OVERSCAN; i++) {
        const resourceId = switcherResourceIds[i];
        if (resourceId) {
          resourcesToRender2.push({
            id: resourceId,
            switcherIndex: i
          });
        }
      }
      const nextNotRenderedIndex = expandedResourceSwitcherIndex + OVERSCAN_FETCH_NEXT_PAGE;
      if (nextNotRenderedIndex >= switcherResourceIds.length && fetchNextPage) {
        logger.log("Fetch next page");
        fetchNextPage();
      }
      return {
        resourcesToRender: resourcesToRender2.filter((item) => item.id),
        expandedResourceSwitcherIndex
      };
    }
  }, [
    expandedResourceDisableSwicher,
    expandedResourceId,
    fetchNextPage,
    state.expandedResourceSwitcherIndex,
    switcherResourceIds
  ]);
  const open = resourcesToRender.length > 0 && expandedResourceId !== null;
  const switcherResourceLength = expandedResourceDisableSwicher ? 1 : switcherResourceIds.length;
  return /* @__PURE__ */ jsx(Modal.Root, {
    open,
    onOpenChange: (v) => {
      if (!v) {
        closeModal();
      }
    },
    children: /* @__PURE__ */ jsxs(Modal.Portal, {
      children: [
        /* @__PURE__ */ jsx(Modal.Overlay, {
          visibleOnMobileViewport: true,
          noMobileBlur: true
        }),
        /* @__PURE__ */ jsxs(ModalDialog, {
          ...defaultModalContentProps,
          children: [
            /* @__PURE__ */ jsx(DialogTitle, {
              style: {
                display: "none"
              },
              children: "Resource detail"
            }),
            /* @__PURE__ */ jsx(DialogDescription, {
              style: {
                display: "none"
              },
              children: "Shows details of the selected resource"
            }),
            /* @__PURE__ */ jsx(DisabledTouchAreaOverlay, {
              style: {
                left: -15
              }
            }),
            /* @__PURE__ */ jsx(DisabledTouchAreaOverlay, {
              style: {
                right: -15
              }
            }),
            /* @__PURE__ */ jsx(ScrollContainer, {
              ref: onScrollContainerRefChange,
              onScroll: state.isInitiallyPositioned ? (e) => {
                if (!e.currentTarget || expandedResourceDisableSwicher) {
                  return;
                }
                const scrollLeft = e.currentTarget.scrollLeft;
                const containerWidth = e.currentTarget.clientWidth;
                const currentIndex = Math.round(scrollLeft / (containerWidth + ITEMS_GAP));
                setState((state2) => {
                  if (state2.expandedResourceSwitcherIndex === currentIndex) return state2;
                  logger.log("onScroll change index", currentIndex);
                  const nextResourceId = switcherResourceIds[currentIndex];
                  if (!nextResourceId) {
                    return state2;
                  }
                  const currentResourceId = isNumber(state2.expandedResourceSwitcherIndex) && switcherResourceIds[state2.expandedResourceSwitcherIndex];
                  if (currentResourceId !== nextResourceId) {
                    debouncedSetexpandedResourceId(nextResourceId);
                  }
                  return {
                    ...state2,
                    expandedResourceSwitcherIndex: currentIndex
                  };
                });
              } : void 0,
              children: /* @__PURE__ */ jsx(motion.div, {
                style: {
                  width: `calc(${switcherResourceLength * 100}vw + ${(switcherResourceLength - 1) * ITEMS_GAP + EXTRA_SIDE_SPACING * 2}px)`,
                  height: "100vh"
                },
                children: resourcesToRender.map(({ id, switcherIndex }) => {
                  return /* @__PURE__ */ jsx(Item, {
                    "data-expanded-resource-id": id,
                    "data-expanded-resource-index": switcherIndex,
                    style: {
                      transform: expandedResourceDisableSwicher ? `translateX(0)` : `translateX(calc(${switcherIndex * 100}vw + ${switcherIndex * ITEMS_GAP + EXTRA_SIDE_SPACING}px)  )`
                    },
                    children: /* @__PURE__ */ jsx(ResourceViewMobile, {
                      handleOnDelete,
                      resourceId: id,
                      isActive: switcherIndex === activeIndex || activeIndex === -1 || activeIndex === void 0
                    }, id)
                  }, id);
                })
              })
            })
          ]
        })
      ]
    })
  });
};

export { ModalResource };
//# sourceMappingURL=ModalResource.js.map
